import { useEffect, useState } from "react";
import { Send, AlertTriangle, X, Inbox } from "lucide-react";
import { apiAdminPushMessages, apiAdminSendPushMessage, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/setupfinder/RichTextEditor";

export default function PushMessagesTab() {
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    try { setHistory(await apiAdminPushMessages() || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const startSend = () => {
    if (!subject.trim() || subject.trim().length < 3) { toast.error("Subject too short"); return; }
    if (!body.trim() || body.replace(/<[^>]*>/g, "").trim().length < 5) {
      toast.error("Body cannot be empty"); return;
    }
    setConfirming(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const result = await apiAdminSendPushMessage({ subject: subject.trim(), body_html: body });
      toast.success(`Sent to ${result.delivered_count}/${result.recipient_count} members`);
      setSubject(""); setBody(""); setConfirming(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-5 space-y-6" data-testid="push-messages-tab">
      {/* Composer */}
      <div className="border border-neutral-900 bg-neutral-950 p-5" data-testid="push-composer">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── new push message ──
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          Sends an email to all active members AND publishes to the Weekly Updates archive.
        </p>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">subject</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Weekly Bitcoin Outlook · June 24"
              data-testid="push-subject"
              maxLength={140}
              className="w-full bg-black border border-neutral-800 px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF]"
            />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">body</div>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Write your weekly update here. Use the toolbar for bold, lists, headings, and links…"
              testid="push-editor"
            />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <button
              onClick={() => setPreviewing(true)}
              disabled={!subject.trim() || !body.trim()}
              data-testid="push-preview-btn"
              className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-neutral-800 hover:border-neutral-600 text-neutral-300 hover:text-white transition-colors disabled:opacity-40"
            >Preview</button>
            <button
              onClick={startSend}
              data-testid="push-send-btn"
              className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-2 hover:bg-neutral-200 transition-colors"
            ><Send className="w-3.5 h-3.5" /> Send to active members</button>
          </div>
        </div>
      </div>

      {/* History */}
      <div data-testid="push-history">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-3">
          recent sends
        </div>
        {history.length === 0 ? (
          <div className="text-xs font-mono text-neutral-600 uppercase tracking-[0.25em] py-4 inline-flex items-center gap-2">
            <Inbox className="w-3.5 h-3.5" /> no messages yet
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="border border-neutral-900 bg-neutral-950 p-4" data-testid={`push-history-row-${h.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-bold text-white">{h.subject}</div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600 mt-1">
                      {new Date(h.sent_at).toLocaleString()} · {h.sent_by}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00E5FF]">
                    {h.delivered_count ?? h.recipient_count} / {h.recipient_count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="push-confirm">
          <div className="bg-neutral-950 border border-neutral-800 w-full max-w-md">
            <div className="flex items-start gap-3 p-5">
              <AlertTriangle className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-white">Send to all active members?</h3>
                <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
                  This will email every active member and add the message to the Weekly Updates archive.
                  This cannot be undone.
                </p>
                <div className="mt-3 text-xs text-neutral-300 font-mono border-l-2 border-neutral-800 pl-3">
                  {subject}
                </div>
              </div>
            </div>
            <div className="border-t border-neutral-900 p-3 flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)} disabled={sending}
                data-testid="push-confirm-cancel"
                className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-neutral-800 hover:border-neutral-600 text-neutral-300 hover:text-white transition-colors disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={confirmSend} disabled={sending}
                data-testid="push-confirm-send"
                className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-2 hover:bg-neutral-200 disabled:opacity-50 transition-colors"
              >{sending ? "Sending…" : "Confirm send"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewing(false)} data-testid="push-preview">
          <div className="bg-neutral-950 border border-neutral-800 w-full max-w-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">preview</div>
              <button onClick={() => setPreviewing(false)} className="text-neutral-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <h2 className="text-2xl font-black tracking-tighter text-white mb-4">{subject}</h2>
              <div className="text-sm text-neutral-200 leading-relaxed weekly-update-body" dangerouslySetInnerHTML={{ __html: body }} />
            </div>
            <style>{`
              .weekly-update-body h2 { font-size: 1.25rem; font-weight: 900; margin: 0.75rem 0 0.5rem; color: #fff; }
              .weekly-update-body ul { padding-left: 1.25rem; list-style: disc; margin: 0.5rem 0; }
              .weekly-update-body a { color: #00E5FF; text-decoration: underline; }
              .weekly-update-body p { margin: 0.5rem 0; }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}
